import { useEffect, useState } from "react";
import {
      APP_VERSION,
      APP_VERSION_MANIFEST_PATH,
      APP_VERSION_POLL_INTERVAL_MS,
      type AppVersionManifest,
      type AppVersionState,
      parseAppVersionManifest,
      resolveAppVersionState,
} from "@/lib/app-version";

const fetchLatestAppVersionManifest = async (): Promise<AppVersionManifest> => {
      const response = await fetch(`${APP_VERSION_MANIFEST_PATH}?t=${Date.now()}`, {
            cache: "no-store",
      });

      if (!response.ok) {
            throw new Error(`Failed to fetch app version manifest (${response.status})`);
      }

      const payload = parseAppVersionManifest(await response.json());

      if (!payload) {
            throw new Error("Failed to parse app version manifest");
      }

      return payload;
};

export const useAppVersionStatus = (enabled = true) => {
      const [versionState, setVersionState] = useState<AppVersionState | null>(null);
      const [isChecking, setIsChecking] = useState(false);

      useEffect(() => {
            if (!enabled || typeof window === "undefined") {
                  return undefined;
            }

            let isDisposed = false;

            const checkVersion = async () => {
                  setIsChecking(true);

                  try {
                        const latestManifest = await fetchLatestAppVersionManifest();

                        if (isDisposed) {
                              return;
                        }

                        setVersionState(resolveAppVersionState(APP_VERSION, latestManifest));
                  } catch (error) {
                        if (!isDisposed) {
                              console.error("Failed to check the deployed app version:", error);
                        }
                  } finally {
                        if (!isDisposed) {
                              setIsChecking(false);
                        }
                  }
            };

            void checkVersion();

            const intervalId = window.setInterval(() => {
                  void checkVersion();
            }, APP_VERSION_POLL_INTERVAL_MS);

            const handleWindowFocus = () => {
                  void checkVersion();
            };

            const handleVisibilityChange = () => {
                  if (document.visibilityState === "visible") {
                        void checkVersion();
                  }
            };

            window.addEventListener("focus", handleWindowFocus);
            document.addEventListener("visibilitychange", handleVisibilityChange);

            return () => {
                  isDisposed = true;
                  window.clearInterval(intervalId);
                  window.removeEventListener("focus", handleWindowFocus);
                  document.removeEventListener("visibilitychange", handleVisibilityChange);
            };
      }, [enabled]);

      return {
            versionState,
            isChecking,
      };
};
